import offlineDB from './indexedDB/offlineDB';
import { EventEmitter } from 'events';
import { logPOSEvent } from './backgroundLogger';

/**
 * MEV Offline Queue Service
 * Manages offline MEV transaction queue with batch synchronization
 * According to Quebec Revenue MEV specifications (Section 4.2)
 */

interface QueuedTransaction {
    id?: number;
    payload: any; // Complete MEV transaction payload (reqTrans format)
    metadata: {
        orderId: string;
        sessionId: string;
        transactionNumber: string;
        datTrans: string; // Transaction date in MEV format (AAAAMMJJhhmmss)
        createdAt: number; // Local timestamp
        storeId: string;
        userId: string;
    };
    status: 'pending' | 'sending' | 'failed';
    createdAt: number;
    lastAttempt?: number;
    error?: string;
}

interface OfflineModeLog {
    enteredAt?: number;
    exitedAt?: number;
    isOffline: boolean;
}

class MevOfflineQueue extends EventEmitter {
    private static instance: MevOfflineQueue;
    private offlineModeLog: OfflineModeLog = { isOffline: false };
    private readonly STORE_NAME = 'mevTransactionQueue';
    private readonly MAX_BATCH_SIZE = 256 * 1024; // 256KB in bytes

    private constructor() {
        super();
    }

    public static getInstance(): MevOfflineQueue {
        if (!MevOfflineQueue.instance) {
            MevOfflineQueue.instance = new MevOfflineQueue();
        }
        return MevOfflineQueue.instance;
    }

    /**
     * Add a failed transaction to the offline queue
     */
    async addTransaction(payload: any, metadata: QueuedTransaction['metadata']): Promise<number> {
        try {
            await offlineDB.init();

            const transaction: QueuedTransaction = {
                payload,
                metadata,
                status: 'pending',
                createdAt: Date.now(),
            };

            console.log('[MEV Offline Queue] Adding transaction to queue:', {
                transactionNumber: metadata.transactionNumber,
                datTrans: metadata.datTrans,
                orderId: metadata.orderId,
            });

            const db = await offlineDB.getDB();
            return new Promise((resolve, reject) => {
                const tx = db.transaction([this.STORE_NAME], 'readwrite');
                const store = tx.objectStore(this.STORE_NAME);
                const request = store.add(transaction);

                request.onsuccess = () => {
                    const id = request.result as number;
                    console.log(`[MEV Offline Queue] Transaction added with ID: ${id}`);
                    this.emit('queue-updated', { count: this.getQueueCount() });
                    resolve(id);
                };

                request.onerror = () => {
                    console.error('[MEV Offline Queue] Failed to add transaction:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('[MEV Offline Queue] Error adding transaction:', error);
            throw error;
        }
    }

    /**
     * Get all unsent transactions from the queue
     */
    async getUnsentTransactions(storeId?: string): Promise<QueuedTransaction[]> {
        try {
            await offlineDB.init();
            const db = await offlineDB.getDB();
            const scope = String(
                storeId
                || (typeof localStorage !== "undefined" ? localStorage.getItem("selectedStore") : "")
                || ""
            ).trim();

            return new Promise((resolve, reject) => {
                const tx = db.transaction([this.STORE_NAME], 'readonly');
                const store = tx.objectStore(this.STORE_NAME);
                const index = store.index('status');
                const request = index.getAll('pending');

                request.onsuccess = () => {
                    const transactions = (request.result as QueuedTransaction[]).filter((row) => (
                        Boolean(scope) && String(row?.metadata?.storeId || row?.payload?.storeId || "") === scope
                    ));
                    console.log(`[MEV Offline Queue] Retrieved ${transactions.length} unsent transactions`);
                    resolve(transactions);
                };

                request.onerror = () => {
                    console.error('[MEV Offline Queue] Failed to get unsent transactions:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('[MEV Offline Queue] Error getting unsent transactions:', error);
            throw error;
        }
    }

    /**
     * Get count of unsent transactions
     */
    async getQueueCount(): Promise<number> {
        try {
            const transactions = await this.getUnsentTransactions();
            return transactions.length;
        } catch (error) {
            console.error('[MEV Offline Queue] Error getting queue count:', error);
            return 0;
        }
    }

    /**
     * Mark a transaction as sent and remove from queue
     */
    async markTransactionSent(id: number): Promise<void> {
        try {
            await offlineDB.init();
            const db = await offlineDB.getDB();

            return new Promise((resolve, reject) => {
                const tx = db.transaction([this.STORE_NAME], 'readwrite');
                const store = tx.objectStore(this.STORE_NAME);
                const request = store.delete(id);

                request.onsuccess = () => {
                    console.log(`[MEV Offline Queue] Transaction ${id} marked as sent and removed`);
                    this.emit('queue-updated', { count: this.getQueueCount() });
                    resolve();
                };

                request.onerror = () => {
                    console.error(`[MEV Offline Queue] Failed to mark transaction ${id} as sent:`, request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('[MEV Offline Queue] Error marking transaction as sent:', error);
            throw error;
        }
    }

    /**
     * Mark a transaction as failed
     */
    async markTransactionFailed(id: number, error: string): Promise<void> {
        try {
            await offlineDB.init();
            const db = await offlineDB.getDB();

            return new Promise((resolve, reject) => {
                const tx = db.transaction([this.STORE_NAME], 'readwrite');
                const store = tx.objectStore(this.STORE_NAME);
                const getRequest = store.get(id);

                getRequest.onsuccess = () => {
                    const transaction = getRequest.result as QueuedTransaction;
                    if (transaction) {
                        transaction.status = 'failed';
                        transaction.error = error;
                        transaction.lastAttempt = Date.now();

                        const putRequest = store.put(transaction);
                        putRequest.onsuccess = () => {
                            console.log(`[MEV Offline Queue] Transaction ${id} marked as failed:`, error);
                            this.emit('queue-updated', { count: this.getQueueCount() });
                            resolve();
                        };
                        putRequest.onerror = () => reject(putRequest.error);
                    } else {
                        resolve();
                    }
                };

                getRequest.onerror = () => reject(getRequest.error);
            });
        } catch (error) {
            console.error('[MEV Offline Queue] Error marking transaction as failed:', error);
            throw error;
        }
    }

    /**
     * Delete a transaction from the queue
     */
    async deleteTransaction(id: number): Promise<void> {
        try {
            await offlineDB.init();
            await offlineDB.delete(this.STORE_NAME, id);
            console.log(`[MEV Offline Queue] Transaction ${id} deleted`);
            this.emit('queue-updated', { count: this.getQueueCount() });
        } catch (error) {
            console.error('[MEV Offline Queue] Error deleting transaction:', error);
            throw error;
        }
    }

    /**
     * Assemble batch of transactions in descending date order (newest first)
     * Returns array of batches if splitting is needed due to 256KB limit
     */
    async assembleBatch(currentTransaction?: any): Promise<any[][]> {
        try {
            const unsentTransactions = await this.getUnsentTransactions();

            // Sort in descending order by datTrans (newest first)
            const sortedTransactions = unsentTransactions.sort((a, b) => {
                return b.metadata.datTrans.localeCompare(a.metadata.datTrans);
            });

            console.log('[MEV Offline Queue] Assembling batch:', {
                unsentCount: sortedTransactions.length,
                hasCurrentTransaction: !!currentTransaction,
            });

            // Build transLot array from sorted transactions
            // Strictly compliant with MEV API schema - no custom fields
            const transLot = sortedTransactions.map(t => t.payload.reqTrans.transActu);

            // If we have a current transaction, it goes BEFORE the batch
            // But if batch exceeds 256KB, current transaction goes with oldest transactions
            const batches: any[][] = [];

            if (currentTransaction) {
                // Calculate size with current transaction
                const testBatch = {
                    reqTrans: {
                        transActu: currentTransaction,
                        transLot: transLot,
                    },
                };

                const batchSize = this.calculateBatchSize(testBatch);

                if (batchSize <= this.MAX_BATCH_SIZE) {
                    // Fits in one batch
                    batches.push([{ currentTransaction, transLot }]);
                } else {
                    // Need to split - current transaction goes with oldest transactions
                    const splitBatches = this.splitBatch(transLot);
                    // Add current transaction to the last batch (oldest transactions)
                    const lastBatch = splitBatches[splitBatches.length - 1];
                    batches.push(...splitBatches.slice(0, -1));
                    batches.push([{ currentTransaction, transLot: lastBatch }]);
                }
            } else {
                // No new sale: the newest queued bill is transActu, the rest are transLot.
                if (transLot.length === 0) {
                    return [];
                }
                const [newest, ...older] = transLot;
                const promoted = { currentTransaction: newest, transLot: older };

                const testBatch = { reqTrans: { transActu: newest, transLot: older } };
                const batchSize = this.calculateBatchSize(testBatch);

                if (batchSize <= this.MAX_BATCH_SIZE) {
                    batches.push([promoted]);
                } else {
                    const splitBatches = this.splitBatch(transLot);
                    batches.push(...splitBatches.map((lot) => {
                        const [head, ...rest] = lot;
                        return [{ currentTransaction: head, transLot: rest }];
                    }));
                }
            }

            console.log(`[MEV Offline Queue] Assembled ${batches.length} batch(es)`);
            return batches;
        } catch (error) {
            console.error('[MEV Offline Queue] Error assembling batch:', error);
            throw error;
        }
    }

    /**
     * Split a batch into multiple batches if exceeding 256KB
     * Returns array of transLot arrays
     */
    private splitBatch(transLot: any[]): any[][] {
        const batches: any[][] = [];
        let currentBatch: any[] = [];

        // Start from the end (oldest transactions) and work backwards
        for (let i = transLot.length - 1; i >= 0; i--) {
            const transaction = transLot[i];
            const testBatch = [...currentBatch, transaction];
            const testSize = this.calculateBatchSize({ reqTrans: { transLot: testBatch } });

            if (testSize > this.MAX_BATCH_SIZE && currentBatch.length > 0) {
                // Current batch is full, start a new one
                batches.push([...currentBatch].reverse()); // Reverse to maintain descending order
                currentBatch = [transaction];
            } else {
                currentBatch.push(transaction);
            }
        }

        // Add the last batch
        if (currentBatch.length > 0) {
            batches.push([...currentBatch].reverse());
        }

        console.log(`[MEV Offline Queue] Split into ${batches.length} batches to stay under 256KB`);
        return batches.reverse(); // Reverse batches so newest are sent first
    }

    /**
     * Calculate batch size in bytes (with whitespace removed as per MEV spec)
     */
    private calculateBatchSize(batch: any): number {
        const jsonString = JSON.stringify(batch);
        // Remove all whitespace to get accurate size
        const compactJson = jsonString.replace(/\s+/g, '');
        return new Blob([compactJson]).size;
    }

    /**
     * Enter offline mode - log timestamp
     */
    enterOfflineMode(): void {
        if (!this.offlineModeLog.isOffline) {
            const enteredAt = Date.now();
            this.offlineModeLog = {
                isOffline: true,
                enteredAt,
            };
            console.log('[MEV Offline Queue] Entered offline mode at:', new Date(enteredAt).toISOString());
            logPOSEvent('OFFLINE_MODE_ON', {
                timestamp: enteredAt,
                enteredAt,
            }).catch((err) => {
                console.error('[MEV Offline Queue] Failed to log offline mode activation:', err);
            });
            this.emit('offline-mode-changed', { isOffline: true });
        }
    }

    /**
     * Exit offline mode - log timestamp
     */
    exitOfflineMode(): void {
        if (this.offlineModeLog.isOffline) {
            const exitedAt = Date.now();
            const enteredAt = this.offlineModeLog.enteredAt || exitedAt;
            this.offlineModeLog.exitedAt = exitedAt;
            const duration = exitedAt - enteredAt;
            console.log('[MEV Offline Queue] Exited offline mode at:', new Date(exitedAt).toISOString());
            console.log('[MEV Offline Queue] Offline duration:', Math.round(duration / 1000), 'seconds');
            logPOSEvent('OFFLINE_MODE_OFF', {
                timestamp: exitedAt,
                enteredAt,
                exitedAt,
                durationMs: duration,
            }).catch((err) => {
                console.error('[MEV Offline Queue] Failed to log offline mode deactivation:', err);
            });
            this.offlineModeLog.isOffline = false;
            this.emit('offline-mode-changed', { isOffline: false });
        }
    }

    /**
     * Check if currently in offline mode
     */
    isOfflineMode(): boolean {
        return this.offlineModeLog.isOffline;
    }

    /**
     * Get offline mode log
     */
    getOfflineModeLog(): OfflineModeLog {
        return { ...this.offlineModeLog };
    }
}

// Singleton instance
const mevOfflineQueue = MevOfflineQueue.getInstance();

export default mevOfflineQueue;
export type { QueuedTransaction, OfflineModeLog };
